import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { FeatureInfoModal } from "../components/FeatureInfoModal";
import { toast } from "sonner";
import { scrollToWithStickyOffset } from "../utils/scrollToWithOffset";
import { showSmartToolsSuccessToast } from "../components/SmartToolsSuccessToast";
import { parseResponseSafely, getFriendlyPaymentError } from "../lib/utils";
import { getFinixFraudSessionId } from "../utils/finixFraudSession";
import { BillingTimingNotice } from "../components/BillingTimingNotice";
import {
  Package,
  CheckCircle,
  Star,
  Lightning,
  ArrowLeft,
  SpinnerGap,
  Bell,
  Translate,
  Envelope,
  CreditCard,
  CalendarBlank,
  Infinity,
  Lock,
  ShieldCheck,
  Warning,
  ShoppingCart,
  Sparkle,
  Info,
  Plus,
  XCircle,
  ArrowClockwise,
  CaretDown,
  CaretUp,
  Bug,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Finix.js SDK URL
const FINIX_JS_URL = "https://js.finix.com/v/2/finix.js";

// Timeout for form initialization (ms)
const FORM_INIT_TIMEOUT = 10000;

const ICON_MAP = {
  bell: Bell,
  translate: Translate,
  envelope: Envelope,
  star: Star,
  "credit-card": CreditCard,
  calendar: CalendarBlank,
  infinity: Infinity,
  lightning: Lightning,
  package: Package,
};

export default function Upgrades() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const preselectedFeature = searchParams.get("feature");
  const autocheckoutFeature = searchParams.get("autocheckout"); // Deep link for auto-checkout
  
  // Keep tenantIdRef in sync with user for use in callbacks (avoids stale closure)
  const tenantIdRef = useRef(null);
  useEffect(() => {
    tenantIdRef.current = user?.tenant_id || null;
  }, [user]);
  
  // Track if autocheckout has been processed (prevent re-trigger on refresh)
  const autocheckoutProcessedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [entitlements, setEntitlements] = useState({ entitled_features: [], entitled_slugs: [] });
  // CONTAINMENT: Force monthly-only billing cycle (yearly option disabled)
  // The yearly toggle was causing broken behavior in production.
  // This is UI-only containment - checkout payload never included billing cycle.
  const billingCycle = "monthly";  // Hard-coded, not stateful
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  
  // Feature Info Modal state
  const [featureInfoModalOpen, setFeatureInfoModalOpen] = useState(false);
  const [selectedFeatureForInfo, setSelectedFeatureForInfo] = useState(null);
  
  // Finix activation status - used to gate payment_processing purchase
  const [finixActivationStatus, setFinixActivationStatus] = useState(null);
  
  // Cart selection state - using ARRAYS for proper React change detection
  const [selectedFeatureIds, setSelectedFeatureIds] = useState([]);
  const [selectedBundleIds, setSelectedBundleIds] = useState([]);
  
  // Pricing calculation state
  const [pricingData, setPricingData] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [pricingError, setPricingError] = useState(null);
  
  // Payment method state
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [addingCard, setAddingCard] = useState(false);
  
  // Finix tokenization state (Slice 3/4 - embedded card entry)
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [tokenizationConfig, setTokenizationConfig] = useState(null);
  const [tokenizationLoading, setTokenizationLoading] = useState(false);
  const [tokenizationError, setTokenizationError] = useState(null); // Init/load errors only
  const [submitError, setSubmitError] = useState(null); // Submit/validation errors (form stays visible)
  const [finixDebugError, setFinixDebugError] = useState(null); // Preview-only: raw Finix error for debugging
  const [debugExpanded, setDebugExpanded] = useState(false); // Preview-only: toggle for debug details
  const [finixFormReady, setFinixFormReady] = useState(false);
  const [finixSubmitting, setFinixSubmitting] = useState(false);
  const finixFormRef = useRef(null);
  const finixPaymentFormInstance = useRef(null);
  const finixTimeoutRef = useRef(null);
  const attachInProgressRef = useRef(false); // Idempotency guard for backend attach
  const fraudSessionIdRef = useRef(null); // Fraud session ID for purchase
  
  // Environment check for detailed logging (preview/staging only)
  const isPreviewEnv = process.env.NODE_ENV !== 'production' || 
    window.location.hostname.includes('preview') || 
    window.location.hostname.includes('staging');
  
  // Helper: Extract safe fields from Finix error for debug display (no sensitive data)
  const extractSafeFinixError = useCallback((err, tenantId) => {
    if (!err) return null;
    const truncate = (str, max = 200) => {
      if (!str) return undefined;
      const s = String(str);
      return s.length > max ? s.substring(0, max) + '...' : s;
    };
    return {
      code: truncate(err.code),
      type: truncate(err.type),
      name: truncate(err.name),
      message: truncate(err.message),
      field: truncate(err.field),
      details: err.details ? truncate(JSON.stringify(err.details)) : undefined,
      tenantId: tenantId || 'unknown',
      ts: new Date().toISOString(),
    };
  }, []);
  
  // Dedupe helper: keep most recently updated method per billing_id
  const dedupePaymentMethods = useCallback((methods) => {
    if (!methods || methods.length === 0) return [];
    
    const byBillingId = new Map();
    for (const pm of methods) {
      const bid = pm.billing_id || pm.payment_method_id;
      const existing = byBillingId.get(bid);
      if (!existing) {
        byBillingId.set(bid, pm);
      } else {
        // Keep the one with more recent updated_at
        const existingDate = new Date(existing.updated_at || existing.created_at || 0);
        const newDate = new Date(pm.updated_at || pm.created_at || 0);
        if (newDate > existingDate) {
          console.warn(`[Upgrades] Duplicate payment method for billing_id ${bid}, keeping newer`);
          byBillingId.set(bid, pm);
        }
      }
    }
    return Array.from(byBillingId.values());
  }, []);

  // Fetch payment methods only (for refresh after card added)
  const fetchPaymentMethods = useCallback(async () => {
    try {
      const paymentRes = await fetch(`${API}/billing/payment-methods`, { credentials: "include" });
      if (paymentRes.ok) {
        const paymentData = await paymentRes.json();
        // Dedupe payment methods defensively
        const dedupedMethods = dedupePaymentMethods(paymentData.payment_methods || []);
        setSavedPaymentMethods(dedupedMethods);
        setDefaultPaymentMethod(paymentData.default_payment_method);
        // Auto-select the default payment method
        if (paymentData.default_payment_method) {
          setSelectedPaymentMethodId(paymentData.default_payment_method.payment_method_id);
        } else if (dedupedMethods.length > 0) {
          // Select first available if no default
          setSelectedPaymentMethodId(dedupedMethods[0].payment_method_id);
        }
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    }
  }, [dedupePaymentMethods]);

  // ==========================================================================
  // FINIX TOKENIZATION (Embedded Card Entry) - Reliable Implementation
  // ==========================================================================

  // Load Finix.js SDK dynamically with timeout
  const loadFinixSdk = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Already loaded
      if (window.Finix) {
        console.log("[Upgrades] Finix SDK already available");
        resolve();
        return;
      }
      
      // Set timeout for SDK load
      const timeoutId = setTimeout(() => {
        reject(new Error("Payment form failed to load. Please check your connection and try again."));
      }, FORM_INIT_TIMEOUT);
      
      // Check for existing script
      const existingScript = document.querySelector(`script[src="${FINIX_JS_URL}"]`);
      if (existingScript) {
        const handleLoad = () => {
          clearTimeout(timeoutId);
          resolve();
        };
        const handleError = () => {
          clearTimeout(timeoutId);
          reject(new Error("Payment form failed to load."));
        };
        existingScript.addEventListener("load", handleLoad);
        existingScript.addEventListener("error", handleError);
        return;
      }
      
      // Create and load script
      const script = document.createElement("script");
      script.src = FINIX_JS_URL;
      script.async = true;
      script.onload = () => {
        clearTimeout(timeoutId);
        console.log("[Upgrades] Finix SDK loaded successfully");
        resolve();
      };
      script.onerror = () => {
        clearTimeout(timeoutId);
        console.error("[Upgrades] Failed to load Finix SDK");
        reject(new Error("Payment form failed to load. Please try again."));
      };
      document.head.appendChild(script);
    });
  }, []);

  // Cleanup function to destroy form instance and reset state
  const cleanupFinixForm = useCallback(() => {
    console.log("[Upgrades] Cleaning up Finix form");
    
    // Clear any pending timeout
    if (finixTimeoutRef.current) {
      clearTimeout(finixTimeoutRef.current);
      finixTimeoutRef.current = null;
    }
    
    // Destroy form instance if it exists
    if (finixPaymentFormInstance.current) {
      // Try to unmount/destroy if method exists
      try {
        if (typeof finixPaymentFormInstance.current.destroy === "function") {
          finixPaymentFormInstance.current.destroy();
        }
      } catch (e) {
        console.log("[Upgrades] Form cleanup notice:", e.message);
      }
      finixPaymentFormInstance.current = null;
    }
    
    // Clear container contents to remove any injected iframes
    if (finixFormRef.current) {
      finixFormRef.current.innerHTML = "";
    }
    
    // Reset state
    setFinixFormReady(false);
    setFinixSubmitting(false);
    setSubmitError(null); // Clear submit errors on cleanup
    setFinixDebugError(null); // Clear debug error on cleanup
    setDebugExpanded(false); // Reset debug toggle
  }, []);

  // Fetch tokenization config
  const fetchTokenizationConfig = useCallback(async () => {
    setTokenizationLoading(true);
    setTokenizationError(null);
    setFinixFormReady(false);
    
    try {
      console.log("[Upgrades] Fetching tokenization config...");
      const response = await fetch(`${API}/billing/tokenization-bootstrap`, {
        credentials: "include",
      });
      
      const data = await response.json();
      console.log("[Upgrades] Tokenization config response:", { ok: data.ok, provider: data.provider });
      
      if (!data.ok) {
        setTokenizationError(data.message || "Payment setup is temporarily unavailable.");
        setTokenizationConfig(null);
        return;
      }
      
      setTokenizationConfig(data);
      
      if (data.provider === "finix") {
        await loadFinixSdk();
      }
      
    } catch (error) {
      console.error("[Upgrades] Tokenization config error:", error);
      setTokenizationError(error.message || "Unable to load payment form. Please try again.");
    } finally {
      setTokenizationLoading(false);
    }
  }, [loadFinixSdk]);

  // Initialize Finix Payment Form - with proper API and timeout
  const initializeFinixForm = useCallback(() => {
    if (!tokenizationConfig || tokenizationConfig.provider !== "finix") {
      console.log("[Upgrades] Skipping form init - no Finix config");
      return;
    }
    
    if (!window.Finix) {
      console.error("[Upgrades] Finix SDK not loaded");
      setTokenizationError("Payment form failed to load. Please try again.");
      return;
    }
    
    if (!finixFormRef.current) {
      console.error("[Upgrades] Form container not mounted");
      return;
    }
    
    // Prevent double initialization
    if (finixPaymentFormInstance.current) {
      console.log("[Upgrades] Form already initialized");
      return;
    }
    
    console.log("[Upgrades] Initializing Finix PaymentForm...");
    
    try {
      const environment = tokenizationConfig.environment || "sandbox";
      const applicationId = tokenizationConfig.application_id;
      
      // ACH/Bank toggle - default to card-only for SaaS billing
      const allowBankAccount = tokenizationConfig.allow_bank_account === true;
      const paymentMethods = allowBankAccount ? ["card", "bank"] : ["card"];
      
      // Clear container first
      finixFormRef.current.innerHTML = "";
      
      // Set initialization timeout
      finixTimeoutRef.current = setTimeout(() => {
        console.error("[Upgrades] Form initialization timed out");
        setTokenizationError("Payment form couldn't load. Please try again.");
        cleanupFinixForm();
      }, FORM_INIT_TIMEOUT);
      
      // Create Finix PaymentForm (correct v2 API)
      // Reference: https://docs.finix.com/guides/online-payments/payment-tokenization/tokenization-forms
      const form = window.Finix.PaymentForm(
        finixFormRef.current,
        environment,
        applicationId,
        {
          paymentMethods,
          defaultCountry: "US",
          showAddress: false,
          styles: {
            default: {
              color: "#18181b",
              fontSize: "16px",
              fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              "::placeholder": { color: "#a1a1aa" },
            },
            error: { color: "#dc2626" },
          },
          // Form state update callback
          onUpdate: (state, binInfo, hasErrors) => {
            if (!finixFormReady && finixTimeoutRef.current) {
              console.log("[Upgrades] Finix form ready (onUpdate received)");
              clearTimeout(finixTimeoutRef.current);
              finixTimeoutRef.current = null;
              setFinixFormReady(true);
            }
            // Clear any previous submit error on user input (form is being corrected)
            if (submitError) {
              setSubmitError(null);
            }
            // Clear debug error when user starts correcting
            if (finixDebugError) {
              setFinixDebugError(null);
              setDebugExpanded(false);
            }
          },
          // Tokenization callback - triggered by Finix's internal submit button
          // This is the SINGLE submission path - no external submit button
          onSubmit: async (error, response) => {
            const timestamp = new Date().toISOString();
            const tenantId = tenantIdRef.current || 'unknown';
            
            // SAFETY BOUNDARY: Wrap entire submit in try/catch/finally
            try {
              // Prevent double-submission
              if (attachInProgressRef.current) {
                console.log('[Upgrades] Attach already in progress, ignoring duplicate submit');
                return;
              }
              
              if (error) {
                // Extract safe debug info (no sensitive data: no card/cvv/name/address/zip values)
                const debugError = extractSafeFinixError(error, tenantId);
                
                // PREVIEW ONLY: Console log with standardized format
                if (isPreviewEnv) {
                  console.error('[FINIX_SUBMIT_ERROR]', {
                    code: debugError.code || 'undefined',
                    type: debugError.type || 'undefined',
                    name: debugError.name || 'undefined',
                    message: debugError.message || 'undefined',
                    field: debugError.field || 'undefined',
                    details: debugError.details || 'undefined',
                    tenantId: debugError.tenantId,
                    ts: debugError.ts,
                  });
                  // Store for UI display
                  setFinixDebugError(debugError);
                }
                
                // TELEMETRY: payment_token_failure (safe fields only - always logged)
                console.error('[Upgrades] payment_token_failure', {
                  tenant_id: tenantId,
                  timestamp,
                  error_code: error.code || 'UNKNOWN',
                  error_type: error.type || 'VALIDATION',
                });
                
                // Normalize error to human-readable message
                const errorMsg = error.message || '';
                const errorCode = (error.code || '').toUpperCase();
                let friendlyMessage = "Card validation failed. Please check your details.";
                
                // ZIP/Postal code errors
                if (errorMsg.toLowerCase().includes('postal') || 
                    errorMsg.toLowerCase().includes('zip') ||
                    errorCode.includes('POSTAL') ||
                    errorCode.includes('ZIP') ||
                    errorCode.includes('ADDRESS')) {
                  friendlyMessage = "ZIP/Postal code is required.";
                }
                // Card number errors
                else if (errorCode.includes('CARD') || errorCode.includes('NUMBER') || errorCode.includes('PAN')) {
                  friendlyMessage = "Please check your card number.";
                }
                // Expiration errors
                else if (errorCode.includes('EXPIR') || errorCode.includes('DATE')) {
                  friendlyMessage = "Please check your expiration date.";
                }
                // CVV/Security code errors
                else if (errorCode.includes('CVV') || errorCode.includes('CVC') || errorCode.includes('SECURITY')) {
                  friendlyMessage = "Please check your security code (CVV).";
                }
                // Use original message if it's already user-friendly
                else if (errorMsg && !errorMsg.includes('object') && errorMsg.length < 100) {
                  friendlyMessage = `Card validation failed: ${errorMsg}`;
                }
                
                // Set submit error (keeps form visible for retry)
                setSubmitError(friendlyMessage);
                toast.error(friendlyMessage);
                return;
              }
              
              // Clear debug error on successful tokenization
              setFinixDebugError(null);
              setDebugExpanded(false);
              
              const tokenData = response?.data || {};
              const paymentInstrumentId = tokenData.id;
              const cardData = tokenData.card || {};
              const addressData = tokenData.address || {};
              
              if (!paymentInstrumentId) {
                // TELEMETRY: payment_token_failure
                console.error('[Upgrades] payment_token_failure', {
                  tenant_id: tenantId,
                  timestamp,
                  error_code: 'NO_TOKEN_ID',
                });
                setSubmitError("Unable to process card. Please try again.");
                toast.error("Unable to process card. Please try again.");
                return;
              }
              
              // TELEMETRY: payment_token_success
              console.log('[Upgrades] payment_token_success', {
                tenant_id: tenantId,
                timestamp,
                token_prefix: paymentInstrumentId.substring(0, 8),
              });
              
              // Country validation (frontend - backend is authoritative)
              const countryRaw = addressData?.country || addressData?.address_country || "US";
              const countryUpper = countryRaw.toUpperCase().trim();
              const COUNTRY_NORMALIZE = { "US": "US", "USA": "US", "CA": "CA", "CAN": "CA" };
              const normalizedCountry = COUNTRY_NORMALIZE[countryUpper] || countryUpper;
              
              if (!["US", "CA"].includes(normalizedCountry)) {
                setSubmitError("We currently only accept payment methods from the United States and Canada.");
                toast.error("We currently only accept payment methods from the United States and Canada.");
                return;
              }
              
              // Set submitting state and idempotency guard
              setFinixSubmitting(true);
              attachInProgressRef.current = true;
              
              // Backend attach - single execution guaranteed by attachInProgressRef
              const saveResponse = await fetch(`${API}/billing/payment-method`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                  payment_instrument_id: paymentInstrumentId,
                  instrument_type: "card",
                  country: normalizedCountry,
                  // Note: backend now fetches metadata from Finix, these are ignored
                  brand: cardData.brand || cardData.type || null,
                  last4: cardData.last_four || cardData.lastFour || null,
                  exp_month: cardData.expiration_month || cardData.expirationMonth || null,
                  exp_year: cardData.expiration_year || cardData.expirationYear || null,
                }),
              });
              
              // Parse response ONCE to avoid "body stream already read" error
              const parsed = await parseResponseSafely(saveResponse);
              
              if (!parsed.ok) {
                // Extract error message from parsed data with full diagnostic chain
                const detail = parsed.data?.detail || {};
                const errorMsg = detail?.message 
                  || detail?.finix_message
                  || (typeof detail === 'string' ? detail : null)
                  || parsed.data?.message 
                  || parsed.error 
                  || `Request failed with status ${parsed.status}`;
                
                // Log diagnostics for debugging (safe - no secrets)
                if (detail?.error_id) {
                  console.error('[Upgrades] Payment method error:', {
                    error_id: detail.error_id,
                    stage: detail.stage,
                    error: detail.error,
                    finix_code: detail.finix_code,
                    effective_environment: detail.effective_environment,
                  });
                }
                
                throw new Error(typeof errorMsg === "string" ? errorMsg : "Failed to save payment method.");
              }
              
              // Success - use backend-returned metadata (processor-authoritative)
              const saveResult = parsed.data;
              
              if (saveResult?.ok) {
                // TELEMETRY: subscription_activation_success
                console.log('[Upgrades] subscription_activation_success', {
                  tenant_id: tenantId,
                  timestamp: new Date().toISOString(),
                  deterministic_outcome: true,
                });
                
                toast.success("Payment method added successfully!");
                setAddCardModalOpen(false);
                fetchPaymentMethods();
              } else {
                throw new Error(saveResult?.message || "Failed to save payment method.");
              }
              
            } catch (catchError) {
              // TELEMETRY: payment_submit_exception (catch-all for unexpected errors)
              console.error('[Upgrades] payment_submit_exception', {
                tenant_id: tenantId,
                timestamp: new Date().toISOString(),
                error_message: catchError?.message || 'Unknown error',
              });
              
              // Use friendly error mapper for user-facing messages
              const friendlyError = getFriendlyPaymentError(catchError?.message);
              setSubmitError(friendlyError);
              toast.error(friendlyError);
            } finally {
              // ALWAYS reset state to prevent stuck UI
              setFinixSubmitting(false);
              attachInProgressRef.current = false;
            }
          },
        }
      );
      
      finixPaymentFormInstance.current = form;
      
      // Also set a shorter backup timer to mark as ready
      // (in case onUpdate doesn't fire immediately)
      setTimeout(() => {
        if (finixPaymentFormInstance.current && !finixFormReady && finixTimeoutRef.current) {
          console.log("[Upgrades] Finix form ready (backup timer)");
          clearTimeout(finixTimeoutRef.current);
          finixTimeoutRef.current = null;
          setFinixFormReady(true);
        }
      }, 1500);
      
    } catch (error) {
      console.error("[Upgrades] Failed to initialize Finix form:", error);
      if (finixTimeoutRef.current) {
        clearTimeout(finixTimeoutRef.current);
        finixTimeoutRef.current = null;
      }
      setTokenizationError("Unable to initialize payment form. Please try again.");
    }
  }, [tokenizationConfig, finixFormReady, cleanupFinixForm]);

  // Effect: Initialize form when modal opens and config is ready
  useEffect(() => {
    if (!addCardModalOpen) {
      // Modal closed - cleanup
      cleanupFinixForm();
      setTokenizationConfig(null);
      setTokenizationError(null);
      setSubmitError(null); // Clear submit errors when modal closes
      return;
    }
    
    // Modal open - wait for config to be loaded, then initialize
    if (tokenizationConfig && !tokenizationLoading && !finixFormReady && !tokenizationError) {
      // Small delay to ensure DOM is ready
      const initTimer = setTimeout(() => {
        initializeFinixForm();
      }, 100);
      
      return () => clearTimeout(initTimer);
    }
  }, [addCardModalOpen, tokenizationConfig, tokenizationLoading, finixFormReady, tokenizationError, initializeFinixForm, cleanupFinixForm]);

  // Effect: Fetch config when modal opens
  useEffect(() => {
    if (addCardModalOpen && !tokenizationConfig && !tokenizationLoading) {
      fetchTokenizationConfig();
    }
  }, [addCardModalOpen, tokenizationConfig, tokenizationLoading, fetchTokenizationConfig]);

  // Handle retry button
  const handleRetryTokenization = useCallback(() => {
    cleanupFinixForm();
    setTokenizationConfig(null);
    setTokenizationError(null);
    setTokenizationLoading(false);
    // Will trigger fetchTokenizationConfig via effect
  }, [cleanupFinixForm]);

  // Handle Add Card button click - opens modal
  const handleAddCardInCheckout = useCallback(() => {
    setAddCardModalOpen(true);
  }, []);

  // Get all feature IDs that are included in currently selected bundles
  const featuresIncludedInSelectedBundles = useMemo(() => {
    const included = new Set();
    selectedBundleIds.forEach(bundleId => {
      const bundle = bundles.find(b => b.bundle_id === bundleId);
      if (bundle?.features) {
        bundle.features.forEach(f => included.add(f.feature_id));
      }
    });
    return included;
  }, [selectedBundleIds, bundles]);

  // Get bundle name for a feature that's included
  const getBundleNameForIncludedFeature = useCallback((featureId) => {
    for (const bundleId of selectedBundleIds) {
      const bundle = bundles.find(b => b.bundle_id === bundleId);
      if (bundle?.features?.some(f => f.feature_id === featureId)) {
        return bundle.name;
      }
    }
    return "selected bundle";
  }, [selectedBundleIds, bundles]);

  // Check if payment_processing is in the cart (either directly or via bundle)
  const isPaymentProcessingInCart = useMemo(() => {
    // Check direct feature selection
    const ppFeature = features.find(f => f.slug === "payment_processing");
    if (ppFeature && selectedFeatureIds.includes(ppFeature.feature_id)) {
      return true;
    }
    
    // Check bundles that include payment_processing
    for (const bundleId of selectedBundleIds) {
      const bundle = bundles.find(b => b.bundle_id === bundleId);
      if (bundle?.features?.some(f => f.slug === "payment_processing")) {
        return true;
      }
    }
    
    return false;
  }, [features, bundles, selectedFeatureIds, selectedBundleIds]);

  // Check if Finix is approved (for gating payment_processing purchase)
  const isFinixApproved = useMemo(() => {
    return finixActivationStatus?.finix?.approved === true;
  }, [finixActivationStatus]);

  const fetchData = useCallback(async () => {
    try {
      // Fetch from CANONICAL entitlements endpoint + available items + payment methods + activation status
      const [entitlementsRes, upgradesRes, paymentRes, activationRes] = await Promise.all([
        fetch(`${API}/subscription/entitlements`, { credentials: "include" }),
        fetch(`${API}/upgrades/available`, { credentials: "include" }),
        fetch(`${API}/billing/payment-methods`, { credentials: "include" }),
        fetch(`${API}/settings/payments/activation-status`, { credentials: "include" })
      ]);
      
      // Process Finix activation status for gating
      if (activationRes.ok) {
        const activationData = await activationRes.json();
        setFinixActivationStatus(activationData);
        console.log("[Upgrades] Finix activation status:", {
          state: activationData.state,
          finix_approved: activationData.finix?.approved,
          payments_usable: activationData.payments_usable
        });
      }
      
      // Process canonical entitlements FIRST
      let ownedFeatureIds = new Set();
      let ownedBundleIds = new Set();
      
      if (entitlementsRes.ok) {
        const entData = await entitlementsRes.json();
        console.log("[Upgrades] Canonical entitlements:", {
          tenant_id: entData.tenant_id,
          active: entData.active_subscription,
          bundles: entData.owned_bundle_ids,
          features: entData.owned_feature_ids
        });
        ownedFeatureIds = new Set(entData.owned_feature_ids || []);
        ownedBundleIds = new Set(entData.owned_bundle_ids || []);
        setEntitlements({
          entitled_features: entData.owned_feature_ids || [],
          entitled_slugs: [],  // Not used anymore
          active_subscription: entData.active_subscription
        });
      }
      
      if (upgradesRes.ok) {
        const data = await upgradesRes.json();
        
        // Override "owned" status using canonical entitlements (NOT from /upgrades/available)
        const featuresWithOwnership = (data.features || []).map(f => ({
          ...f,
          owned: ownedFeatureIds.has(f.feature_id)
        }));
        const bundlesWithOwnership = (data.bundles || []).map(b => ({
          ...b,
          owned: ownedBundleIds.has(b.bundle_id)
        }));
        
        setFeatures(featuresWithOwnership);
        setBundles(bundlesWithOwnership);

        if (preselectedFeature) {
          const feature = featuresWithOwnership.find((f) => f.slug === preselectedFeature);
          if (feature && !feature.owned) {
            setTimeout(() => {
              const element = document.getElementById(`feature-${feature.feature_id}`);
              if (element) {
                scrollToWithStickyOffset(element, { behavior: "smooth", block: "center" });
                element.classList.add("ring-2", "ring-[#FF6600]");
              }
            }, 100);
          }
        }
      }
      
      if (paymentRes.ok) {
        const paymentData = await paymentRes.json();
        setSavedPaymentMethods(paymentData.payment_methods || []);
        setDefaultPaymentMethod(paymentData.default_payment_method);
        if (paymentData.default_payment_method) {
          setSelectedPaymentMethodId(paymentData.default_payment_method.payment_method_id);
        }
      }
    } catch (error) {
      console.error("Error fetching upgrades:", error);
      toast.error("Failed to load upgrades");
    } finally {
      setLoading(false);
    }
  }, [preselectedFeature]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate pricing when selection changes
  useEffect(() => {
    const calculatePricing = async () => {
      // Clear pricing if nothing selected
      if (selectedFeatureIds.length === 0 && selectedBundleIds.length === 0) {
        setPricingData(null);
        setPricingError(null);
        console.log("[Upgrades] Selection cleared, pricingData = null");
        return;
      }
      
      setCalculatingPrice(true);
      setPricingError(null);
      
      const endpoint = `${API}/subscription/quote`;
      const payload = {
        selected_feature_ids: selectedFeatureIds,
        selected_bundle_ids: selectedBundleIds
      };
      
      console.log("[Upgrades] pricing request", endpoint, payload);
      
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload)
        });
        
        console.log("[Upgrades] pricing response status:", response.status);
        
        if (response.ok) {
          const data = await response.json();
          setPricingData(data);
          console.log("[Upgrades] Quote result:", {
            total: data.total_monthly_cents,
            billable: data.billable_items?.length,
            included: data.included_feature_ids?.length
          });
        } else {
          const errorText = await response.text();
          console.error("[Upgrades] Pricing calculation failed:", response.status, errorText);
          setPricingError("Failed to calculate pricing");
        }
      } catch (error) {
        console.error("[Upgrades] Error calculating pricing:", error);
        setPricingError("Network error");
      } finally {
        setCalculatingPrice(false);
      }
    };
    
    // Debounce the calculation
    const timer = setTimeout(calculatePricing, 200);
    return () => clearTimeout(timer);
  }, [selectedFeatureIds, selectedBundleIds]);

  // ==========================================================================
  // AUTOCHECKOUT DEEP LINK HANDLER
  // Handles ?autocheckout=payment_processing from PaymentsActivationBanner
  // Preselects the feature and opens checkout modal automatically
  // ==========================================================================
  const processAutocheckout = useCallback((featureSlug) => {
    if (!featureSlug || autocheckoutProcessedRef.current) return;
    if (loading || features.length === 0) return; // Wait for data
    
    console.log("[Upgrades] Processing autocheckout for:", featureSlug);
    
    // Find the feature by slug
    const feature = features.find(f => f.slug === featureSlug);
    if (!feature) {
      console.warn("[Upgrades] Autocheckout feature not found:", featureSlug);
      return;
    }
    
    // Check if already owned
    if (feature.owned) {
      console.log("[Upgrades] Feature already owned, skipping autocheckout:", featureSlug);
      // Clear the param to prevent re-trigger
      setSearchParams(prev => {
        prev.delete("autocheckout");
        return prev;
      }, { replace: true });
      autocheckoutProcessedRef.current = true;
      return;
    }
    
    // Mark as processed to prevent re-trigger
    autocheckoutProcessedRef.current = true;
    
    // Select the feature (if not already selected)
    setSelectedFeatureIds(prev => {
      if (prev.includes(feature.feature_id)) {
        console.log("[Upgrades] Feature already in cart:", feature.feature_id);
        return prev;
      }
      console.log("[Upgrades] Autocheckout: Adding feature to cart:", feature.feature_id);
      return [...prev, feature.feature_id];
    });
    
    // Clear the URL param to prevent re-trigger on refresh
    setSearchParams(prev => {
      prev.delete("autocheckout");
      return prev;
    }, { replace: true });
    
    // Open checkout modal after a short delay (allow pricing to calculate)
    setTimeout(() => {
      console.log("[Upgrades] Autocheckout: Opening purchase modal");
      setPurchaseModalOpen(true);
      // Refresh payment methods when opening modal
      fetchPaymentMethods();
    }, 500);
  }, [loading, features, setSearchParams, fetchPaymentMethods]);
  
  // Effect to handle autocheckout param on load
  useEffect(() => {
    if (autocheckoutFeature && !loading && features.length > 0) {
      processAutocheckout(autocheckoutFeature);
    }
  }, [autocheckoutFeature, loading, features, processAutocheckout]);
  
  // Effect to handle custom event from banner when already on page
  useEffect(() => {
    const handleAutocheckoutEvent = (event) => {
      const featureSlug = event.detail?.feature;
      if (featureSlug) {
        // Reset processed flag to allow re-trigger from event
        autocheckoutProcessedRef.current = false;
        processAutocheckout(featureSlug);
      }
    };
    
    window.addEventListener("fsp:autocheckout", handleAutocheckoutEvent);
    return () => window.removeEventListener("fsp:autocheckout", handleAutocheckoutEvent);
  }, [processAutocheckout]);

  // Toggle feature selection - also removes if included in bundle
  const toggleFeatureSelection = useCallback((featureId) => {
    // Don't allow selecting features included in selected bundles
    if (featuresIncludedInSelectedBundles.has(featureId)) {
      console.log("[Upgrades] Feature", featureId, "is included in bundle, cannot select");
      return;
    }
    
    setSelectedFeatureIds(prev => {
      const isSelected = prev.includes(featureId);
      if (isSelected) {
        console.log("[Upgrades] Deselected feature:", featureId);
        return prev.filter(id => id !== featureId);
      } else {
        console.log("[Upgrades] Selected feature:", featureId);
        return [...prev, featureId];
      }
    });
  }, [featuresIncludedInSelectedBundles]);

  // Toggle bundle selection - also removes any individually selected features that are now included
  const toggleBundleSelection = useCallback((bundleId) => {
    setSelectedBundleIds(prev => {
      const isSelected = prev.includes(bundleId);
      if (isSelected) {
        console.log("[Upgrades] Deselected bundle:", bundleId);
        return prev.filter(id => id !== bundleId);
      } else {
        console.log("[Upgrades] Selected bundle:", bundleId);
        
        // Remove any features that are now included in this bundle
        const bundle = bundles.find(b => b.bundle_id === bundleId);
        if (bundle?.features) {
          const includedFeatureIds = bundle.features.map(f => f.feature_id);
          setSelectedFeatureIds(prevFeatures => {
            const removed = prevFeatures.filter(fid => includedFeatureIds.includes(fid));
            if (removed.length > 0) {
              console.log("[Upgrades] Auto-removed features now included in bundle:", removed);
            }
            return prevFeatures.filter(fid => !includedFeatureIds.includes(fid));
          });
        }
        
        return [...prev, bundleId];
      }
    });
  }, [bundles]);

  const openPurchaseModal = useCallback(() => {
    if (!pricingData || pricingData.billable_items?.length === 0) {
      toast.error("No items to purchase");
      return;
    }
    
    // ==========================================================================
    // GATING: payment_processing requires Finix approval BEFORE checkout
    // UX: Immediate redirect to settings - NO toast, NO error popup
    // ==========================================================================
    if (isPaymentProcessingInCart && !isFinixApproved) {
      console.log("[Upgrades] Redirecting: payment_processing in cart but Finix not approved");
      // Clean redirect - no toast, no error - user goes to settings to complete onboarding
      navigate("/settings?tab=payments");
      return;
    }
    
    // Open the purchase modal - payment is REQUIRED for all features including payment_processing
    setPurchaseModalOpen(true);
    // Refresh payment methods to ensure we have the latest
    fetchPaymentMethods();
    if (defaultPaymentMethod) {
      setSelectedPaymentMethodId(defaultPaymentMethod.payment_method_id);
    }
    
    // Start fraud session collection in parallel (best-effort, non-blocking)
    // Uses platform merchant_id from tokenizationConfig
    if (tokenizationConfig?.merchant_id) {
      getFinixFraudSessionId({
        environment: tokenizationConfig.environment || 'sandbox',
        merchantId: tokenizationConfig.merchant_id,
        timeoutMs: 3000
      }).then(sessionId => {
        fraudSessionIdRef.current = sessionId;
        console.log('[Upgrades] Fraud session:', sessionId ? 'obtained' : 'not available');
      });
    }
  }, [pricingData, defaultPaymentMethod, fetchPaymentMethods, isPaymentProcessingInCart, isFinixApproved, navigate, tokenizationConfig]);

  const handlePurchase = async () => {
    if (!pricingData || pricingData.billable_items?.length === 0) return;
    
    if (!selectedPaymentMethodId) {
      toast.error("Please add a payment method to continue");
      return;
    }
    
    setPurchasing(true);

    try {
      const idempotencyKey = crypto.randomUUID();
      
      console.log("[Purchase] Initiating with payment_method_id:", selectedPaymentMethodId);
      
      // Use XMLHttpRequest to avoid fetch interceptors that may consume the response
      const xhr = new XMLHttpRequest();
      const url = `${API}/subscription/cart/purchase`;
      
      // Get fraud session ID before sending request (best-effort, fail-open)
      let fraudSessionId = fraudSessionIdRef.current;
      if (!fraudSessionId && tokenizationConfig?.merchant_id) {
        try {
          fraudSessionId = await Promise.race([
            getFinixFraudSessionId({
              environment: tokenizationConfig.environment || 'sandbox',
              merchantId: tokenizationConfig.merchant_id,
              timeoutMs: 300
            }),
            new Promise(resolve => setTimeout(() => resolve(null), 300))
          ]);
        } catch {
          // Fail-open: proceed without fraud session
        }
      }
      
      const result = await new Promise((resolve, reject) => {
        xhr.open("POST", url, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.withCredentials = true;
        
        xhr.onload = function() {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
          } catch (e) {
            resolve({ ok: false, status: xhr.status, data: { detail: `Server error (${xhr.status})` } });
          }
        };
        
        xhr.onerror = function() {
          reject(new Error("Network error"));
        };
        
        // Build payload - include fraud_session_id if available
        const payload = {
          selected_feature_ids: selectedFeatureIds,
          selected_bundle_ids: selectedBundleIds,
          payment_method_id: selectedPaymentMethodId,
          idempotency_key: idempotencyKey,
          accept_optimization: true
        };
        if (fraudSessionId && typeof fraudSessionId === "string" && fraudSessionId.trim() !== "") {
          payload.fraud_session_id = fraudSessionId;
        }
        
        console.log("[Purchase] Submitting:", { hasFraudSession: !!fraudSessionId });
        
        xhr.send(JSON.stringify(payload));
      });

      if (result.ok) {
        // Check if this is a redirect response (not an actual purchase success)
        if (result.data.redirect) {
          // Clean redirect to onboarding - no toast, no error
          setPurchaseModalOpen(false);
          navigate(result.data.redirect_url || "/settings?tab=payments");
          return;
        }
        
        toast.success(result.data.message || "Purchase successful!");
        setPurchaseModalOpen(false);
        setSelectedFeatureIds([]);
        setSelectedBundleIds([]);
        setPricingData(null);
        fetchData();
        
        // Track Smart Tools purchase success (if Smart Tools was purchased)
        const purchasedSmartTools = selectedFeatureIds.includes("smart_tools") || 
          bundles.some(b => selectedBundleIds.includes(b.bundle_id) && 
            b.features?.some(f => f.feature_id === "smart_tools"));
        
        if (purchasedSmartTools) {
          try {
            await fetch(`${API}/analytics/event`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                event: "smart_tools_upgrade_purchase_success",
                properties: { source: preselectedFeature || "manual" }
              })
            });
          } catch (e) { /* non-critical */ }
          
          // Return to where user came from (if set by SmartToolsUpgradeCTA)
          const returnPath = sessionStorage.getItem("smart_tools_upgrade_return_path");
          if (returnPath) {
            sessionStorage.removeItem("smart_tools_upgrade_return_path");
            setTimeout(() => {
              navigate(returnPath);
              // Show rich success toast with quick-start tips
              showSmartToolsSuccessToast(navigate);
            }, 1000);
          } else {
            // No return path - show success toast immediately
            showSmartToolsSuccessToast(navigate);
          }
        }
      } else {
        const error = result.data;
        console.error("[Purchase] Failed:", error);
        
        // Extract detailed error info
        const detail = error.detail;
        let errorMessage = "Purchase failed";
        let debugInfo = "";
        let actionRequired = null;
        
        if (typeof detail === "object") {
          // Handle 409 NO_ITEMS_TO_PURCHASE specifically
          if (detail.error_code === "NO_ITEMS_TO_PURCHASE") {
            errorMessage = detail.message || "All selected items are already owned.";
            console.log("[Purchase] NO_ITEMS_TO_PURCHASE - owned_feature_ids:", detail.owned_feature_ids);
            
            // Close modal and clear selection since items are owned
            setPurchaseModalOpen(false);
            setSelectedFeatureIds([]);
            setSelectedBundleIds([]);
            setPricingData(null);
            
            // Refresh data to get updated ownership
            fetchData();
            
            toast.info(
              <div>
                <p className="font-medium">{errorMessage}</p>
                <p className="text-xs text-zinc-500 mt-1">Your cart has been cleared.</p>
              </div>,
              { duration: 5000 }
            );
            return; // Exit early - don't show additional error toast
          }
          
          // Handle FINIX_APPLICATION_BLOCKED - rejected or suspended (show error)
          if (detail.error_code === "FINIX_APPLICATION_BLOCKED") {
            errorMessage = detail.message || "Your payment processing application has been blocked. Please contact support.";
            console.log("[Purchase] FINIX_APPLICATION_BLOCKED - status:", detail.finix_status);
            
            // Close modal
            setPurchaseModalOpen(false);
            
            // Show error toast (this IS an error case)
            toast.error(
              <div>
                <p className="font-medium">{errorMessage}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Contact support for assistance.
                </p>
              </div>,
              { duration: 10000 }
            );
            return; // Exit early
          }
          
          // Detailed error object from backend
          errorMessage = detail.message || "Payment failed";
          actionRequired = detail.action_required;
          
          if (detail.tc_status) {
            if (detail.tc_status === "baddata") {
              errorMessage = "Invalid card token. Please add your card again.";
            } else if (detail.tc_status === "decline") {
              errorMessage = "Card declined. Please try a different card.";
            } else if (detail.tc_status === "error") {
              errorMessage = detail.message || "Payment processor error";
            } else if (detail.tc_status === "invalid_billing_id") {
              errorMessage = detail.message;
            }
          }
          
          // Build debug info
          debugInfo = `[TC: ${detail.tc_status || 'unknown'}]`;
          if (detail.tc_error_code) debugInfo += ` Code: ${detail.tc_error_code}`;
          if (detail.billing_id_used) debugInfo += ` Card: ${detail.billing_id_used}`;
        } else if (typeof detail === "string") {
          errorMessage = detail;
          
          // Handle "No saved payment method" error - prompt user to add card
          if (detail.toLowerCase().includes("no saved payment method")) {
            toast.error("Please add a payment card to continue");
            // Open the add card modal (close purchase modal first)
            setPurchaseModalOpen(false);
            setAddCardModalOpen(true);
            return; // Exit early
          }
        }
        
        // Show error toast with details
        toast.error(
          <div>
            <p className="font-medium">{errorMessage}</p>
            {debugInfo && <p className="text-xs text-zinc-400 mt-1">{debugInfo}</p>}
            {actionRequired === "add_new_card" && (
              <p className="text-xs text-orange-400 mt-1">Go to Billing page → Add Card</p>
            )}
          </div>,
          { duration: 10000 }
        );
      }
    } catch (error) {
      // True network error (offline, CORS, etc.)
      console.error("[Purchase] Network error:", error);
      toast.error(`Network error: ${error.message || "Please check your connection."}`);
    } finally {
      setPurchasing(false);
    }
  };

  const getPrice = (item) => {
    if (!item) return "0.00";
    // CONTAINMENT: Always return monthly price (yearly disabled)
    // FIX: Format to 2 decimal places to avoid float precision display bugs
    return (item.price_monthly || 0).toFixed(2);
  };

  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || Package;
  };

  // Check if a feature is owned (from API data)
  const isFeatureOwned = useCallback((featureId) => {
    const feature = features.find(f => f.feature_id === featureId);
    return feature?.owned || false;
  }, [features]);

  // Check if a bundle is owned (from API data)
  const isBundleOwned = useCallback((bundleId) => {
    const bundle = bundles.find(b => b.bundle_id === bundleId);
    return bundle?.owned || false;
  }, [bundles]);

  const totalSelectedCount = selectedFeatureIds.length + selectedBundleIds.length;
  const hasBillableItems = pricingData?.billable_items?.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 pb-24 ${totalSelectedCount > 0 ? 'pt-20' : ''}`} data-testid="upgrades-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="border-2 border-zinc-200"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              Upgrade Your Plan
            </h1>
            <p className="text-zinc-500">
              Add powerful features to grow your business
            </p>
          </div>
        </div>

        {/* Billing Toggle - REMOVED: Monthly-only mode (yearly caused broken behavior) */}
        {/* Original toggle preserved in comments for future re-enablement if needed */}
      </div>

      {/* Current Plan Summary */}
      {entitlements.entitled_slugs?.length > 0 && (
        <Card className="border-2 border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={20} className="text-emerald-600" />
              <span className="font-medium text-emerald-900">Your Active Features</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {entitlements.entitled_slugs.map((slug) => {
                const feature = features.find((f) => f.slug === slug);
                return (
                  <Badge key={slug} variant="outline" className="border-emerald-300 text-emerald-700">
                    {feature?.name || slug}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sticky Cart Bar - FIXED POSITION for consistent sticky behavior */}
      {totalSelectedCount > 0 && (
        <div className="fixed top-16 lg:left-64 right-0 z-40 px-4 lg:px-8 py-3 bg-white border-b-2 border-[#FF6600] shadow-lg">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF6600] text-white flex items-center justify-center">
                <ShoppingCart size={20} />
              </div>
              <div>
                <p className="font-medium">
                  {totalSelectedCount} item{totalSelectedCount > 1 ? "s" : ""} selected
                </p>
                {calculatingPrice ? (
                  <p className="text-sm text-zinc-500 flex items-center gap-1">
                    <SpinnerGap size={14} className="animate-spin" />
                    Calculating...
                  </p>
                ) : pricingError ? (
                  <p className="text-sm text-red-600">{pricingError}</p>
                ) : pricingData ? (
                  <p className="text-sm text-zinc-600">
                    {pricingData.billable_items?.length || 0} billable
                    {pricingData.included_feature_ids?.length > 0 && (
                      <span className="text-emerald-600 ml-1">
                        • {pricingData.included_feature_ids.length} included free
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-zinc-400">Select items to see pricing</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Savings indicator */}
              {pricingData?.optimization?.applied && pricingData.optimization.savings_cents > 0 && (
                <div className="flex items-center gap-2 px-3 py-1 bg-emerald-100 rounded-sm">
                  <Sparkle size={16} className="text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">
                    Saving ${(pricingData.optimization.savings_cents / 100).toFixed(2)}/mo
                  </span>
                </div>
              )}
              
              <div className="text-right">
                {calculatingPrice ? (
                  <p className="text-xl font-bold text-zinc-400">
                    <SpinnerGap size={20} className="animate-spin inline mr-1" />
                  </p>
                ) : (
                  <p className="text-2xl font-bold text-[#FF6600]">
                    ${pricingData ? (pricingData.total_monthly_cents / 100).toFixed(2) : "0.00"}
                  </p>
                )}
                <p className="text-xs text-zinc-500">/month</p>
              </div>
              
              <Button
                onClick={openPurchaseModal}
                disabled={!hasBillableItems || calculatingPrice}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white px-6"
                data-testid="checkout-btn"
              >
                {calculatingPrice ? (
                  <SpinnerGap size={18} className="animate-spin" />
                ) : (
                  "Checkout"
                )}
              </Button>
            </div>
          </div>
          
          {/* Optimization notice */}
          {pricingData?.optimization?.applied && (
            <div className="mt-3 pt-3 border-t border-zinc-200 flex items-start gap-2">
              <Sparkle size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-700">
                <span className="font-medium">Bundle applied!</span> We've automatically optimized your selection to save you money.
              </p>
            </div>
          )}
          
          {/* Included features notice */}
          {pricingData?.included_features?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-zinc-200">
              <p className="text-sm text-zinc-600 flex items-center gap-1 mb-2">
                <Info size={14} />
                These features are included at no extra charge:
              </p>
              <div className="flex flex-wrap gap-2">
                {pricingData.included_features.map(f => (
                  <Badge key={f.feature_id} variant="outline" className="border-emerald-300 text-emerald-700">
                    <CheckCircle size={12} className="mr-1" />
                    {f.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bundles Section */}
      {bundles.length > 0 && (
        <section>
          <h2 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
            <Star size={20} className="text-[#FF6600]" />
            Recommended Bundles
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {bundles.map((bundle) => {
              const isOwned = isBundleOwned(bundle.bundle_id);
              const isSelected = selectedBundleIds.includes(bundle.bundle_id);
              const totalValue = bundle.total_feature_value_monthly || 0;
              const savings = totalValue - bundle.price_monthly;
              const savingsPct = totalValue > 0 ? Math.round((savings / totalValue) * 100) : 0;

              return (
                <Card
                  key={bundle.bundle_id}
                  className={`border-2 relative overflow-hidden transition-all ${
                    isOwned 
                      ? "border-emerald-300 bg-emerald-50 cursor-default" 
                      : isSelected 
                        ? "border-[#FF6600] bg-[#FF6600]/5 cursor-pointer" 
                        : "border-zinc-200 hover:border-[#FF6600]/50 cursor-pointer"
                  }`}
                  onClick={() => !isOwned && toggleBundleSelection(bundle.bundle_id)}
                >
                  {savingsPct > 0 && !isOwned && (
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-3 py-1">
                      SAVE {savingsPct}%
                    </div>
                  )}
                  {isSelected && (
                    <div className="absolute top-0 left-0 bg-[#FF6600] text-white text-xs font-bold px-3 py-1">
                      SELECTED
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="font-heading text-xl uppercase">{bundle.name}</CardTitle>
                        <p className="text-sm text-zinc-500 mt-1">{bundle.description}</p>
                      </div>
                      {isOwned && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle size={14} className="mr-1" />
                          Owned
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-[#FF6600]">${getPrice(bundle)}</span>
                      <span className="text-zinc-500">/mo</span>
                      {totalValue > 0 && (
                        <span className="text-sm text-zinc-400 line-through">
                          ${totalValue.toFixed(2)}
                        </span>
                      )}
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium text-zinc-700">Includes:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {bundle.features?.map((feature) => (
                          <div key={feature.feature_id} className="flex items-center gap-2 text-sm">
                            <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                            <span>{feature.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isOwned) toggleBundleSelection(bundle.bundle_id);
                      }}
                      disabled={isOwned}
                      className={`w-full ${
                        isOwned 
                          ? "bg-zinc-200 text-zinc-500 cursor-not-allowed" 
                          : isSelected 
                            ? "bg-[#FF6600] hover:bg-[#CC5200] text-white" 
                            : "bg-[#FF6600] hover:bg-[#CC5200] text-white"
                      }`}
                      data-testid={`select-bundle-${bundle.bundle_id}`}
                    >
                      {isOwned ? "Already Owned" : isSelected ? "Selected ✓" : "Select Bundle"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Individual Features Section */}
      <section>
        <h2 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
          <Lightning size={20} className="text-[#FF6600]" />
          Individual Features
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature) => {
            const IconComp = getIcon(feature.icon);
            const isOwned = isFeatureOwned(feature.feature_id);
            const isSelected = selectedFeatureIds.includes(feature.feature_id);
            const isIncludedInBundle = featuresIncludedInSelectedBundles.has(feature.feature_id);
            const includingBundleName = isIncludedInBundle ? getBundleNameForIncludedFeature(feature.feature_id) : null;
            
            // Determine if this feature can be clicked
            const isClickable = !isOwned && !isIncludedInBundle;

            return (
              <Card
                key={feature.feature_id}
                id={`feature-${feature.feature_id}`}
                className={`border-2 transition-all flex flex-col h-full ${
                  isOwned 
                    ? "border-emerald-300 bg-emerald-50 cursor-default" 
                    : isIncludedInBundle 
                      ? "border-emerald-200 bg-emerald-50/50 cursor-default" 
                      : isSelected 
                        ? "border-[#FF6600] bg-[#FF6600]/5 cursor-pointer" 
                        : "border-zinc-200 hover:border-[#FF6600]/50 cursor-pointer"
                }`}
                onClick={() => isClickable && toggleFeatureSelection(feature.feature_id)}
              >
                <CardContent className="p-4 flex flex-col flex-1">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${
                      isOwned || isIncludedInBundle 
                        ? "bg-emerald-100 text-emerald-600" 
                        : isSelected 
                          ? "bg-[#FF6600] text-white" 
                          : "bg-[#FF6600]/10 text-[#FF6600]"
                    }`}>
                      <IconComp size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium">{feature.name}</h3>
                      <p className="text-xs text-zinc-500 mt-1">{feature.description}</p>
                    </div>
                    {isOwned && <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />}
                    {isIncludedInBundle && !isOwned && (
                      <Badge variant="outline" className="border-emerald-300 text-emerald-600 text-xs flex-shrink-0">
                        Included
                      </Badge>
                    )}
                    {isSelected && !isIncludedInBundle && (
                      <CheckCircle size={20} className="text-[#FF6600] flex-shrink-0" />
                    )}
                  </div>

                  {/* Included in bundle helper text */}
                  {isIncludedInBundle && !isOwned && (
                    <p className="text-xs text-emerald-600 mb-2 flex items-center gap-1">
                      <Info size={12} />
                      Included with {includingBundleName}
                    </p>
                  )}

                  {feature.feature_type === "limit_increase" && (
                    <div className="mb-3 text-xs">
                      {feature.job_limit === -1 && (
                        <Badge variant="outline" className="border-emerald-300 text-emerald-600 mr-1">
                          Unlimited Jobs
                        </Badge>
                      )}
                      {feature.photo_limit === -1 && (
                        <Badge variant="outline" className="border-emerald-300 text-emerald-600">
                          Unlimited Photos
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Price and Action Row - anchored at bottom */}
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-100 mt-auto">
                    <div>
                      {isIncludedInBundle ? (
                        <span className="text-sm text-emerald-600 font-medium">Free with bundle</span>
                      ) : (
                        <>
                          <span className="text-lg font-bold text-[#FF6600]">${getPrice(feature)}</span>
                          <span className="text-sm text-zinc-500">/mo</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFeatureForInfo(feature);
                          setFeatureInfoModalOpen(true);
                        }}
                        className="border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 px-3 py-1.5 rounded-lg font-medium"
                        data-testid={`learn-more-${feature.feature_id}`}
                      >
                        Learn More
                      </Button>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isClickable) toggleFeatureSelection(feature.feature_id);
                        }}
                        disabled={isOwned || isIncludedInBundle}
                        className={
                          isOwned 
                            ? "bg-zinc-200 text-zinc-500 cursor-not-allowed px-4 py-1.5 rounded-lg" 
                            : isIncludedInBundle 
                              ? "bg-emerald-100 text-emerald-700 cursor-not-allowed px-4 py-1.5 rounded-lg" 
                              : isSelected 
                                ? "bg-[#FF6600] hover:bg-[#CC5200] text-white px-4 py-1.5 rounded-lg" 
                                : "bg-[#FF6600] hover:bg-[#CC5200] text-white px-4 py-1.5 rounded-lg"
                        }
                        data-testid={`select-feature-${feature.feature_id}`}
                      >
                        {isOwned 
                          ? "Owned" 
                          : isIncludedInBundle 
                            ? "Included" 
                            : isSelected 
                              ? "Selected ✓" 
                              : "Select"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Purchase Modal - Using DialogBody/DialogFooter for proper mobile viewport handling */}
      <Dialog open={purchaseModalOpen} onOpenChange={setPurchaseModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="secure-checkout-modal">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <Lock size={20} className="text-emerald-600" />
              Secure Checkout
            </DialogTitle>
            <DialogDescription>
              Complete your purchase
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            {pricingData && (
              <div className="space-y-4">
                {/* Items Summary */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-zinc-700">Your Selection:</p>
                  {pricingData.billable_items?.map((item) => (
                    <div key={item.item_id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-sm bg-[#FF6600]/10 flex items-center justify-center">
                          {item.item_type === "bundle" ? (
                            <Package size={16} className="text-[#FF6600]" />
                          ) : (
                            <Lightning size={16} className="text-[#FF6600]" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-zinc-500 capitalize">{item.item_type}</p>
                        </div>
                      </div>
                      <span className="font-medium">${(item.price_monthly_cents / 100).toFixed(2)}/mo</span>
                    </div>
                  ))}
                  
                  {/* Included features */}
                  {pricingData.included_features?.length > 0 && (
                    <div className="p-3 bg-emerald-50 rounded-sm">
                      <p className="text-sm font-medium text-emerald-700 mb-2">Included at no extra charge:</p>
                      <div className="flex flex-wrap gap-1">
                        {pricingData.included_features.map(f => (
                          <Badge key={f.feature_id} variant="outline" className="border-emerald-300 text-emerald-600 text-xs">
                            {f.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Optimization savings */}
                  {pricingData.optimization?.applied && pricingData.optimization.savings_cents > 0 && (
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
                      <Sparkle size={18} className="text-emerald-600" />
                      <span className="text-sm text-emerald-700">
                        You're saving <strong>${(pricingData.optimization.savings_cents / 100).toFixed(2)}/mo</strong> with bundle pricing!
                      </span>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="flex items-center justify-between p-4 bg-zinc-100 rounded-sm">
                  <span className="font-medium">Total (monthly)</span>
                  <span className="text-2xl font-bold text-[#FF6600]">
                    ${(pricingData.total_monthly_cents / 100).toFixed(2)}
                  </span>
                </div>

                {/* Security Notice */}
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
                  <ShieldCheck size={20} className="text-emerald-600" />
                  <span className="text-sm text-emerald-800">Secure payment • PCI DSS compliant</span>
                </div>

                {/* Billing Timing Notice - Mid-cycle purchase UX clarity */}
                {pricingData.billing_timing?.has_existing_subscription && pricingData.billing_timing?.next_billing_date && (
                  <BillingTimingNotice 
                    nextBillingDate={pricingData.billing_timing.next_billing_date}
                    variant="default"
                  />
                )}

                {/* DEFENSIVE BLOCK: payment_processing in cart but Finix not approved */}
                {isPaymentProcessingInCart && !isFinixApproved ? (
                  <div className="p-4 bg-red-50 border-2 border-red-200 rounded-sm" data-testid="finix-onboarding-required-block">
                    <div className="flex items-start gap-3">
                      <Warning size={24} className="text-red-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800">Complete Finix Onboarding Required</p>
                        <p className="text-sm text-red-700 mt-1">
                          Payment Processing requires Finix merchant approval before you can complete this purchase.
                        </p>
                        <Button
                          size="sm"
                          onClick={() => {
                            setPurchaseModalOpen(false);
                            navigate("/settings?tab=payments");
                          }}
                          className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                          data-testid="go-to-onboarding-btn"
                        >
                          {finixActivationStatus?.finix?.onboarding_form_id_present 
                            ? "Continue Onboarding Setup" 
                            : "Start Onboarding Setup"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Payment Method Selection - Only shown when Finix check passes */
                  <>
                {/* Payment Method Selection */}
                {savedPaymentMethods.length > 0 ? (
                  <div className="space-y-3">
                    <label className="text-sm font-medium">Select Payment Method</label>
                    <div className="space-y-2">
                      {savedPaymentMethods.map((method) => (
                        <button
                          key={method.payment_method_id}
                          type="button"
                          onClick={() => setSelectedPaymentMethodId(method.payment_method_id)}
                          data-testid={`payment-method-${method.payment_method_id}`}
                          className={`w-full p-3 rounded-sm border-2 flex items-center gap-3 transition-colors ${
                            selectedPaymentMethodId === method.payment_method_id 
                              ? "border-[#FF6600] bg-[#FF6600]/5" 
                              : "border-zinc-200 hover:border-zinc-300"
                          }`}
                        >
                          <CreditCard size={24} className={
                            selectedPaymentMethodId === method.payment_method_id 
                              ? "text-[#FF6600]" 
                              : "text-zinc-400"
                          } />
                          <div className="flex-1 text-left">
                            <p className="font-medium capitalize">{method.brand || 'Card'} •••• {method.last4 || '****'}</p>
                            <p className="text-xs text-zinc-500">
                              {method.exp_month && method.exp_year 
                                ? `Expires ${method.exp_month}/${method.exp_year}`
                                : 'Expiration not available'}
                            </p>
                          </div>
                          {method.is_default && (
                            <Badge variant="outline" className="border-zinc-300">Default</Badge>
                          )}
                          {selectedPaymentMethodId === method.payment_method_id && (
                            <CheckCircle size={20} className="text-[#FF6600]" />
                          )}
                        </button>
                      ))}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddCardInCheckout}
                      disabled={addingCard}
                      className="w-full border-dashed"
                    >
                      {addingCard ? (
                        <>
                          <SpinnerGap size={16} className="mr-2 animate-spin" />
                          Opening secure form...
                        </>
                      ) : (
                        <>
                          <Plus size={16} className="mr-2" />
                          Add a different card
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
                    <div className="flex items-start gap-2">
                      <Warning size={20} className="text-amber-600 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-amber-800">No Payment Method</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Please add a payment method to complete your purchase.
                        </p>
                        <Button
                          size="sm"
                          onClick={handleAddCardInCheckout}
                          disabled={addingCard}
                          className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          {addingCard ? (
                            <>
                              <SpinnerGap size={16} className="mr-2 animate-spin" />
                              Opening...
                            </>
                          ) : (
                            <>
                              <CreditCard size={16} className="mr-2" />
                              Add Payment Method
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                  </>
                )}
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPurchaseModalOpen(false)}
              className="flex-1 border-2"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={purchasing || !selectedPaymentMethodId || savedPaymentMethods.length === 0 || (isPaymentProcessingInCart && !isFinixApproved)}
              className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="confirm-purchase-btn"
            >
              {purchasing ? (
                <>
                  <SpinnerGap size={18} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Lock size={16} className="mr-2" />
                  Pay ${pricingData ? (pricingData.total_monthly_cents / 100).toFixed(2) : "0.00"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Card Modal - Embedded Finix Tokenization Form */}
      <Dialog open={addCardModalOpen} onOpenChange={setAddCardModalOpen}>
        <DialogContent className="sm:max-w-lg p-0">
          {/* Modal Header - Compact */}
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-100 space-y-1">
            <DialogTitle className="flex items-center gap-2.5 text-lg font-semibold">
              <div className="p-1.5 bg-emerald-50 rounded-full">
                <Lock size={18} className="text-emerald-600" />
              </div>
              Add Payment Method
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500 flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-emerald-600" />
              256-bit SSL encrypted • PCI DSS compliant
            </DialogDescription>
          </DialogHeader>
          
          {/* Modal Body - Scrollable */}
          <DialogBody className="px-6 py-4 space-y-4">
            {/* Loading state */}
            {tokenizationLoading && (
              <div className="flex flex-col items-center justify-center py-10">
                <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
                <span className="mt-3 text-zinc-600">Loading secure form...</span>
              </div>
            )}
            
            {/* Error state with Retry */}
            {tokenizationError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-red-800">Unable to Load Payment Form</p>
                    <p className="mt-1 text-sm text-red-700">{tokenizationError}</p>
                    <Button
                      onClick={handleRetryTokenization}
                      variant="outline"
                      size="sm"
                      className="mt-3 border-red-300 text-red-700 hover:bg-red-100"
                    >
                      <ArrowClockwise size={16} className="mr-2" />
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Finix Embedded Form */}
            {!tokenizationLoading && !tokenizationError && tokenizationConfig?.provider === "finix" && (
              <>
                {/* Submit/validation error - shown above form, form stays visible for retry */}
                {submitError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <XCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-800">{submitError}</p>
                        <p className="text-xs text-red-600 mt-1">Please correct the issue and try again.</p>
                        
                        {/* PREVIEW ONLY: Debug details collapsible */}
                        {isPreviewEnv && finixDebugError && (
                          <div className="mt-2 pt-2 border-t border-red-200">
                            <button
                              type="button"
                              onClick={() => setDebugExpanded(!debugExpanded)}
                              className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-mono"
                            >
                              <Bug size={12} />
                              Debug details (preview only)
                              {debugExpanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
                            </button>
                            {debugExpanded && (
                              <div className="mt-2 p-2 bg-red-100 rounded text-xs font-mono text-red-900 overflow-x-auto">
                                <div><strong>Code:</strong> {finixDebugError.code || 'undefined'}</div>
                                <div><strong>Type:</strong> {finixDebugError.type || 'undefined'}</div>
                                <div><strong>Name:</strong> {finixDebugError.name || 'undefined'}</div>
                                <div><strong>Message:</strong> {finixDebugError.message || 'undefined'}</div>
                                <div><strong>Field:</strong> {finixDebugError.field || 'undefined'}</div>
                                {finixDebugError.details && (
                                  <div><strong>Details:</strong> {finixDebugError.details}</div>
                                )}
                                <div className="mt-1 pt-1 border-t border-red-200 text-red-600">
                                  <strong>Tenant:</strong> {finixDebugError.tenantId} | <strong>Time:</strong> {finixDebugError.ts}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Secure Card Entry info - compact */}
                <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-emerald-800">
                    <ShieldCheck size={16} className="text-emerald-600 flex-shrink-0" />
                    <span>Your card details are entered securely. We never see your full card number.</span>
                  </div>
                </div>
                
                {/* Country restriction notice */}
                <div className="p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-zinc-600">
                    <Info size={14} className="text-zinc-500" />
                    <span>Only United States and Canada are supported right now.</span>
                  </div>
                </div>
                
                {/* Test mode notice (non-prod only, no card numbers) */}
                {tokenizationConfig.environment === "sandbox" && (
                  <div className="p-2.5 bg-amber-50 border border-amber-100 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <Info size={14} className="text-amber-600" />
                      <span>Test mode is enabled.</span>
                    </div>
                  </div>
                )}
                
                {/* Form Container - Initializing State */}
                {!finixFormReady && (
                  <div className="flex flex-col items-center justify-center py-10 border border-zinc-200 rounded-lg bg-zinc-50">
                    <SpinnerGap size={24} className="animate-spin text-zinc-400" />
                    <span className="mt-2 text-sm text-zinc-500">Initializing secure form...</span>
                  </div>
                )}
                
                {/* Finix hosted form container */}
                <div 
                  ref={finixFormRef}
                  className={`min-h-[180px] border border-zinc-200 rounded-lg bg-white overflow-hidden ${!finixFormReady ? "hidden" : ""}`}
                  data-testid="upgrades-finix-tokenization-form"
                  style={{ padding: "12px" }}
                />
              </>
            )}
            
            {/* Unsupported provider */}
            {!tokenizationLoading && !tokenizationError && tokenizationConfig && tokenizationConfig.provider !== "finix" && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Payment Setup Unavailable</p>
                    <p className="mt-1 text-sm text-red-700">Payment method setup is temporarily unavailable. Please contact support.</p>
                  </div>
                </div>
              </div>
            )}
          </DialogBody>
          
          {/* Modal Footer - Sticky */}
          <DialogFooter className="px-6 py-4 bg-zinc-50 border-t border-zinc-100 flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => setAddCardModalOpen(false)}
              className="flex-1 h-10"
              disabled={finixSubmitting}
            >
              {finixSubmitting ? "Processing..." : "Cancel"}
            </Button>
            {/* External submit button REMOVED - Finix iframe submit is the single source of truth */}
            {finixSubmitting && (
              <div className="flex items-center text-sm text-zinc-500">
                <SpinnerGap size={16} className="animate-spin mr-2" />
                Saving payment method...
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feature Info Modal */}
      <FeatureInfoModal
        isOpen={featureInfoModalOpen}
        onClose={() => {
          setFeatureInfoModalOpen(false);
          setSelectedFeatureForInfo(null);
        }}
        feature={selectedFeatureForInfo}
        onEnableFeature={() => {
          if (selectedFeatureForInfo && !isFeatureOwned(selectedFeatureForInfo.feature_id)) {
            toggleFeatureSelection(selectedFeatureForInfo.feature_id);
          }
        }}
        isOwned={selectedFeatureForInfo ? isFeatureOwned(selectedFeatureForInfo.feature_id) : false}
        isIncludedInBundle={selectedFeatureForInfo ? featuresIncludedInSelectedBundles.has(selectedFeatureForInfo.feature_id) : false}
        isSelected={selectedFeatureForInfo ? selectedFeatureIds.includes(selectedFeatureForInfo.feature_id) : false}
      />
    </div>
  );
}
